import React, { useState } from "react";
import styled from "styled-components/macro";
import InputTextApollo from "./InputTextApollo";
import {
  getEffectiveScalingProperties,
  getFloor,
  getCeiling,
} from "../../adapters/baselineLoadAdapter";
import {
  getNumberInputValue,
  getNumberOrUndefinedFromString,
} from "../../utils/inputUtils";

// Pinned (disabled) scaling inputs render too dark by default: the theme's
// #ecedee fill plus the browser's own disabled-text dimming stacking on the
// gray. Lighter fill + explicit text-fill keeps it readable but clearly locked.
const PinnedTextField = styled(InputTextApollo)`
  .MuiInputBase-root.Mui-disabled,
  .MuiInputBase-input.Mui-disabled {
    background: var(--color-background-secondary);
    background-color: var(--color-background-secondary);
    color: var(--color-foreground-disable);
    -webkit-text-fill-color: var(--color-foreground-disable);
    opacity: 1;
  }
`;

/**
 * Numeric scaling input with commit-on-blur draft semantics:
 * - while focused it holds a raw local draft (no clamping, no formData writes);
 * - on blur it commits ONE field: a valid number is clamped to [floor, ceiling],
 *   a cleared/invalid draft DELETES the field (absent = "at reference", sparse);
 * - at rest it displays the EFFECTIVE value — exactly what the calculation uses.
 * In a read-only default profile it is disabled and shows the effective (floor).
 */
const DraftNumberInput = ({
  label,
  helperText,
  product,
  field,
  baselineProfile,
  editable,
  onProductChange,
}: {
  label: string;
  helperText?: string;
  product: ProductOptionInterface;
  field: string;
  baselineProfile: string;
  editable: boolean;
  onProductChange: (updatedProduct: ProductOptionInterface) => void;
}) => {
  const [draft, setDraft] = useState<string | null>(null);

  const effective = getEffectiveScalingProperties(
    product.key,
    baselineProfile,
    product.properties || {},
  )[field];

  const commit = () => {
    const raw = draft;
    setDraft(null);
    if (raw === null) return;
    const parsed = getNumberOrUndefinedFromString(raw);
    const properties = { ...(product.properties || {}) };
    if (parsed === undefined || !Number.isFinite(parsed)) {
      // Cleared/invalid draft → drop the field: absent means "at reference".
      if (!(field in properties)) return;
      delete properties[field];
    } else {
      const clamped = getEffectiveScalingProperties(
        product.key, baselineProfile, { [field]: parsed },
      )[field];
      // Effective value unchanged → skip the write (keeps formData sparse).
      if (clamped === effective) return;
      properties[field] = clamped;
    }
    onProductChange({ ...product, properties });
  };

  return (
    <PinnedTextField
      label={label}
      helperText={helperText}
      variant="outlined"
      disabled={!editable}
      inputProps={{
        type: "number",
        min: getFloor(product.key, baselineProfile, field),
        max: getCeiling(product.key, field),
      }}
      InputProps={{
        name: field,
        value: draft !== null ? draft : getNumberInputValue(effective),
        onFocus: () => setDraft(getNumberInputValue(effective)),
        onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
          setDraft(e.currentTarget.value),
        onBlur: commit,
        // Enter commits too — conventional key; blur routes through commit.
        onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        },
      }}
    />
  );
};

export default DraftNumberInput;
